import React, { useContext, useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'
import queryString from 'query-string'
import callAPI from '../../../helpers/callAPI'
import Loading from '../../../templates/UI/Loading/Loading'
import Dollars from '../../../templates/Dollars/Dollars'
import CommissionsHistoryDetailsModal from './CommissionsHistoryDetailsModal'
import { SystemMessageContext } from '../../../context/SystemMessageContext'
import ReactToolTip from 'react-tooltip'

const TABLE_COLUMN_COUNT = 6
const MIN_REPORT_YEAR = 2026

const MONTH_OPTIONS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
]

const CommissionsHistory = ({ location, history }) => {
    const { showSystemMessage } = useContext(SystemMessageContext)
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1
    const urlParams = queryString.parse(location?.search || '')

    const parsedYear = Number(urlParams.year)
    const initialYear = Number.isInteger(parsedYear) && parsedYear >= MIN_REPORT_YEAR && parsedYear <= currentYear ? parsedYear : currentYear

    const parsedMonth = Number(urlParams.month)
    const hasValidMonth = Number.isInteger(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12
    const initialMonth = hasValidMonth ? parsedMonth : currentMonth

    const [selectedYear, setSelectedYear] = useState(initialYear)
    const [selectedMonth, setSelectedMonth] = useState(initialMonth)
    const [password, setPassword] = useState('')
    const [appliedPassword, setAppliedPassword] = useState('')
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
    const [selectedAdminId, setSelectedAdminId] = useState(null)
    const [selectedDate, setSelectedDate] = useState(null)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [data, setData] = useState(null)

    useEffect(() => {
        setSelectedYear(initialYear)
        setSelectedMonth(initialMonth)
    }, [initialYear, initialMonth])

    const yearOptions = []

    for (let year = currentYear; year >= MIN_REPORT_YEAR; year -= 1) {
        yearOptions.push(year)
    }

    const adminIdFromUrl = String(urlParams.adminId || '').trim()
    const normalizedAdminId = /^\d+$/.test(adminIdFromUrl) ? adminIdFromUrl : '0'
    const passwordValue = appliedPassword.trim()
    const apiUrl = passwordValue
        ? `reports/commissions?${queryString.stringify({
            adminId: normalizedAdminId,
            year: selectedYear,
            month: selectedMonth,
            password: passwordValue,
        })}`
        : null

    useEffect(() => {
        let isMounted = true

        const fetchReport = async () => {
            if (!apiUrl) {
                if (isMounted) {
                    setData(null)
                    setDataLoaded(true)
                }
                return
            }

            if (isMounted) {
                setDataLoaded(false)
            }

            const result = await callAPI(apiUrl, 'GET')

            if (!isMounted) {
                return
            }

            if (result?.success) {
                setData(result)
            } else if (showSystemMessage) {
                showSystemMessage('error', result?.errors?.message || 'Could not load commissions report data.')
                setData(null)
            }

            setDataLoaded(true)
        }

        fetchReport()

        return () => {
            isMounted = false
        }
    }, [apiUrl])

    const rows = Array.isArray(data?.report) ? data.report : []

    const headerFullName = rows[0]?.full_name || ''

    const normalizeReportDate = (date) => {
        if (!date) {
            return null
        }

        const dateString = String(date)

        if (dateString.includes('T')) {
            return dateString.split('T')[0]
        }

        return dateString
    }

    const handleOpenDetailsModal = (adminId, date) => {
        setSelectedAdminId(adminId)
        setSelectedDate(normalizeReportDate(date))
        setIsDetailsModalOpen(true)
    }

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false)
    }

    const handleYearChange = (event) => {
        const nextYear = Number(event.target.value)
        setSelectedYear(nextYear)
        history.push(`?${queryString.stringify({ ...urlParams, year: nextYear, month: selectedMonth })}`)
    }

    const handleMonthChange = (event) => {
        const nextMonth = Number(event.target.value)
        setSelectedMonth(nextMonth)
        history.push(`?${queryString.stringify({ ...urlParams, year: selectedYear, month: nextMonth })}`)
    }

    const handleApplyPassword = () => {
        setAppliedPassword(password.trim())
    }

    return (
        <React.Fragment>
            <CommissionsHistoryDetailsModal isOpen={isDetailsModalOpen} onClose={handleCloseDetailsModal} adminId={selectedAdminId} date={selectedDate || ''} mode="history" />

            <div className="page-header d-flex align-items-center">
                <span className="mb-h1">Commissions History</span>
            </div>
            
            <div className="row">
                <div className="col-xl-8">
                    <Card>
                        <Card.Header className="d-flex align-items-center justify-content-start">
                            <h1 className="mb-h1-dark mr-2">{headerFullName ? `${headerFullName}` : ''}</h1>
                            <div className="d-flex align-items-center">
                                <label className="mb-0 mr-2" htmlFor="commissions-report-year">Year</label>
                                <select
                                    id="commissions-report-year"
                                    className="form-control form-control-sm mr-3"
                                    value={selectedYear}
                                    onChange={handleYearChange}
                                >
                                    {yearOptions.map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>

                                <label className="mb-0 mr-2" htmlFor="commissions-report-month">Month</label>
                                <select
                                    id="commissions-report-month"
                                    className="form-control form-control-sm mr-3"
                                    style={{ width: 'auto', minWidth: '7rem' }}
                                    value={selectedMonth}
                                    onChange={handleMonthChange}
                                >
                                    {MONTH_OPTIONS.map((month) => (
                                        <option key={month.value} value={month.value}>{month.label}</option>
                                    ))}
                                </select>
                            </div>
                            <input
                                type="password"
                                className="form-control form-control-sm ml-2"
                                style={{ width: '160px' }}
                                placeholder="Password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                            />
                            <button
                                type="button"
                                className="btn btn-sm btn-info ml-2"
                                disabled={!password.trim()}
                                onClick={handleApplyPassword}
                            >
                                Apply
                            </button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {!appliedPassword.trim() ? (
                                <div className="p-3">Enter password to view this report.</div>
                            ) : !dataLoaded ? (
                                <table className="table table-striped table-card table-hover mb-0">
                                    <tbody>
                                        <tr>
                                            <td colSpan={TABLE_COLUMN_COUNT}>
                                                <Loading />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : rows.length === 0 ? (
                                <table className="table table-striped table-card table-hover mb-0">
                                    <tbody>
                                        <tr>
                                            <td colSpan={TABLE_COLUMN_COUNT}>No stats for this date range.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <table className="table table-striped table-card table-hover mb-0 text-small">
                                    <ReactToolTip />
                                    <thead>
                                        <tr>
                                            <th>DATE</th>
                                            <th className="text-right border-right">Margin Target</th>
                                            <th className="text-center border-right" data-tip="% of target reached with existing business margin only.">❓</th>
                                            <th className="text-right">Existing Margin</th>                                            
                                            <th className="text-right">New Margin</th>
                                            <th className="text-right border-right">Total Margin</th>
                                            <th className="text-right">Existing Commission</th>
                                            <th className="text-right">Existing Comm Rate</th>
                                            <th className="text-right">New Commission</th>
                                            <th className="text-right">Total Commission</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, index) => {
                                            return (
                                                <tr key={`${row.admin_id}-${row.date}-${index}`}>
                                                    <td>{String(row.date || '').split('T')[0]}</td>
                                                    <td className="text-right border-right"><Dollars value={row.existing_target} /></td>
                                                    <td className="text-center border-right" data-tip={`${Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.existing_margin)} / ${Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.existing_target)}`}>{row.existing_ratio}%</td>
                                                    <td className="text-right"><Dollars value={row.existing_margin} /> </td>
                                                    <td className="text-right"><Dollars value={row.new_margin} /></td>
                                                    <td className="text-right border-right">
                                                        <button type="button" className="btn btn-sm btn-link p-0 align-baseline" onClick={() => handleOpenDetailsModal(row.admin_id, row.date)}>
                                                            <Dollars value={row.total_margin} />
                                                        </button>
                                                    </td>
                                                    <td className="text-right"><Dollars value={row.existing_commission} /></td>
                                                    <td className="text-right">{Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.existing_comm_rate)}%</td>
                                                    <td className="text-right"><Dollars value={row.new_commission} /></td>
                                                    <td className="text-right"> <Dollars value={row.total_commission} /> </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
            
        </React.Fragment>
    )
}

export default CommissionsHistory